import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { api } from "../context/AuthContext";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Box,
  Avatar,
  TextField,
  InputAdornment,
  Button
} from '@mui/material';
import { Search, Person } from '@mui/icons-material';

function SuperAdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    async function fetchUsers() {
      try {
        const response = await api.get('/super-admin/users');
        setUsers(response.data);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, []);

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await api.patch(`/super-admin/users/${userId}/toggle-active`, { has_active_subscription: !currentStatus });
      setUsers(prevUsers =>
          prevUsers.map(user =>
              user.id === userId ? { ...user, has_active_subscription: !currentStatus } : user
          )
      );
    } catch (error) {
      console.error('Failed to update user status:', error);
    }
  };

  const filteredUsers = users.filter(user =>
      (user.first_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.last_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.role?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
    );
  }

  return (
      <Container sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4"
                      sx={{
                        color: "#4F46E5",
                        fontWeight: "bold",
                        mb: 2
                      }}
          >System Users</Typography>
          <TextField
              size="small"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
          />
        </Box>

        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Subscription</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Joined Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {user.first_name ? user.first_name[0] : <Person />}
                        </Avatar>
                        <Typography variant="body2">
                          {user.first_name} {user.last_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                          label={user.role}
                          color={user.role === 'super_admin' ? 'error' : (user.role === 'admin' ? 'primary' : 'default')}
                          size="small"
                          variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                          label={user.has_active_subscription ? 'Active' : 'Inactive'}
                          color={user.has_active_subscription ? 'success' : 'default'}
                          size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                          size="small"
                          variant={user.has_active_subscription ? "outlined" : "contained"}
                          color={user.has_active_subscription ? "error" : "success"}
                          onClick={() => handleToggleActive(user.id, user.has_active_subscription)}
                      >
                        {user.has_active_subscription ? "Deactivate" : "Activate"}
                      </Button>
                    </TableCell>
                  </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
  );
}

export default SuperAdminUsers;
